import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Chip, Divider, List, ListItem,
  ListItemText, ListItemAvatar, Avatar, TextField, IconButton,
  Container, Tabs, Tab, Stack, Stepper, Step, StepLabel, InputBase, Fab, CircularProgress, Grow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArticleIcon from '@mui/icons-material/Article';
import CommentIcon from '@mui/icons-material/Comment';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { marked } from 'marked';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ContractDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [newDraftFile, setNewDraftFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [reportLoading, setReportLoading] = useState({});

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');

  // Version Comments State
  const [vcDialogOpen, setVcDialogOpen] = useState(false);
  const [vcDialogDraftId, setVcDialogDraftId] = useState(null);
  const [vcEditItem, setVcEditItem] = useState(null); // null = add mode, object = edit mode
  const [vcForm, setVcForm] = useState({ page: '', clause_number: '', details: '', comment: '', comment_date: '', reply: '', comment_2: '', comment_2_date: '', reply_2: '' });
  const [expandedDraft, setExpandedDraft] = useState(null);

  // Chatbot State
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMaximized, setChatMaximized] = useState(false);
  const [messages, setMessages] = useState([{ sender: 'bot', text: 'Hello! Please select a contract version to enable chat.' }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const sessionId = React.useMemo(() => 'session_' + Math.random().toString(36).substr(2, 9), []);
  const chatEndRef = useRef(null);

  useEffect(() => {
      if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
  }, [messages, chatOpen]);

  const handleSendMessage = async () => {
      if (!chatInput.trim() || chatLoading) return;
      if (!activeFile) {
          setMessages(prev => [...prev, { sender: 'bot', text: 'Please select a contract version first.' }]);
          return;
      }
      const userMessage = chatInput;
      setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
      setChatInput('');
      setChatLoading(true);

      setMessages(prev => [...prev, { sender: 'bot', text: 'Thinking...', isLoader: true }]);

      try {
          const encodedUserMessage = encodeURIComponent(userMessage);
          const res = await fetch(`${API_URL}/chat_contracts/chat_session_rag?session_id=${encodeURIComponent(sessionId)}&user_input=${encodedUserMessage}&file_id_input=${encodeURIComponent(activeFile.id)}`, {
              method: 'GET',
              headers: {
                  ...headers
              }
          });

          if (!res.body) throw new Error('ReadableStream not yet supported in this browser.');
          const reader = res.body.getReader();
          const decoder = new TextDecoder('utf-8');

          while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n');
              let currentEvent = 'message';
              for (let line of lines) {
                  if (line.startsWith('event:')) {
                      currentEvent = line.substring(6).trim();
                  } else if (line.startsWith('data:')) {
                      let dataStr = line.substring(5).trim();
                      if (dataStr) {
                          try {
                              const parsed = JSON.parse(dataStr);
                              if (currentEvent === 'message') {
                                  let text = parsed.token || parsed.text || parsed.response || '';
                                  if (text) {
                                      setMessages(prev => {
                                          const base = [...prev];
                                          if (base[base.length - 1].isLoader) {
                                              base[base.length - 1].isLoader = false;
                                              base[base.length - 1].text = text;
                                          } else {
                                              base[base.length - 1].text += text;
                                          }
                                          return base;
                                      });
                                  }
                              } else if (currentEvent === 'error') {
                                  setMessages(prev => {
                                      const base = [...prev];
                                      base[base.length - 1].isLoader = false;
                                      base[base.length - 1].text = 'Sorry, there was an error processing your request.';
                                      return base;
                                  });
                              }
                          } catch (e) {
                              if (dataStr !== 'null' && dataStr !== '[DONE]') {
                                  setMessages(prev => {
                                      const base = [...prev];
                                      if (base[base.length - 1].isLoader) {
                                          base[base.length - 1].isLoader = false;
                                          base[base.length - 1].text = dataStr;
                                      } else {
                                          base[base.length - 1].text += dataStr;
                                      }
                                      return base;
                                  });
                              }
                          }
                      }
                  }
              }
          }
      } catch (error) {
          console.error('Chat error:', error);
          setMessages(prev => {
              const base = [...prev];
              base[base.length - 1].isLoader = false;
              base[base.length - 1].text = 'Sorry, there was an error processing your request.';
              return base;
          });
      } finally {
          setChatLoading(false);
      }
  };

  const handleChatWithVersion = async (draft) => {
      setChatOpen(true);
      setActiveFile(draft);
      setMessages([{ sender: 'bot', text: `Processing version ${draft.version}...`, isLoader: true }]);
      setChatLoading(true);

      const encodedFilename = encodeURIComponent(draft.file_path);
      const url = `${API_URL}/contracts/${id}/files/${encodedFilename}`;
      try {
          const response = await fetch(url, { headers });
          const blob = await response.blob();
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = async () => {
              const base64data = reader.result.split(',')[1];
              try {
                  await axios.post(`${API_URL}/chat_contracts/generate_vector_store`, {
                      base_64_str_input: base64data,
                      file_name_input: draft.file_path,
                      file_id_input: draft.id,
                  }, { headers });
                  setMessages([{ sender: 'bot', text: `Version ${draft.version} loaded successfully! You can now start chatting.` }]);
              } catch (apiError) {
                  console.error('Vector store error:', apiError);
                  setMessages([{ sender: 'bot', text: `Error processing document with AI. Please ensure the local vector store service is running.` }]);
              } finally {
                  setChatLoading(false);
              }
          };
      } catch (err) {
          console.error('File fetch error:', err);
          setMessages([{ sender: 'bot', text: `Could not fetch the document to initialize chat.` }]);
          setChatLoading(false);
      }
  };

  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('user_role') || '';
  const isChatbotAllowed = userRole === 'Admin' || userRole === 'Contracts Manager';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API_URL}/contracts/${id}`, { headers });
      setContract(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContract();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
      try {
          await axios.put(`${API_URL}/contracts/${id}`, { status: newStatus }, { headers });
          fetchContract(); 
      } catch (err) {
          console.error(err);
          alert('Failed to update status');
      }
  };

  const handleDescriptionSave = async () => {
      try {
          await axios.put(`${API_URL}/contracts/${id}`, { description: editDescription }, { headers });
          setIsEditingDescription(false);
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to update description');
      }
  };

  // Convert DD/MM/YY → YYYY-MM-DD for <input type="date">
  const ddmmyyToInput = (str) => {
      if (!str) return '';
      const parts = str.split('/');
      if (parts.length !== 3) return '';
      const [dd, mm, yy] = parts;
      return `20${yy}-${mm}-${dd}`;
  };
  // Convert YYYY-MM-DD → DD/MM/YY for API
  const inputToDdmmyy = (str) => {
      if (!str) return '';
      const parts = str.split('-');
      if (parts.length !== 3) return '';
      const [yyyy, mm, dd] = parts;
      return `${dd}/${mm}/${yyyy.slice(2)}`;
  };

  const openVcDialog = (draftId, item = null) => {
      setVcDialogDraftId(draftId);
      setVcEditItem(item);
      setVcForm(item ? { ...item, comment_date: ddmmyyToInput(item.comment_date), comment_2_date: ddmmyyToInput(item.comment_2_date) } : { page: '', clause_number: '', details: '', comment: '', comment_date: '', reply: '', comment_2: '', comment_2_date: '', reply_2: '' });
      setVcDialogOpen(true);
  };

  const handleVcSave = async () => {
      const payload = { ...vcForm, comment_date: inputToDdmmyy(vcForm.comment_date), comment_2_date: inputToDdmmyy(vcForm.comment_2_date) };
      try {
          if (vcEditItem) {
              await axios.put(`${API_URL}/contracts/${id}/drafts/${vcDialogDraftId}/version-comments/${vcEditItem.id}`, payload, { headers });
          } else {
              await axios.post(`${API_URL}/contracts/${id}/drafts/${vcDialogDraftId}/version-comments`, payload, { headers });
          }
          setVcDialogOpen(false);
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to save version comment');
      }
  };

  const handleVcDelete = async (draftId, commentId) => {
      if (!window.confirm('Delete this comment row?')) return;
      try {
          await axios.delete(`${API_URL}/contracts/${id}/drafts/${draftId}/version-comments/${commentId}`, { headers });
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to delete version comment');
      }
  };

  const handleExportVersionComments = (draft) => {
      const rows = (draft.version_comments || []).map(vc => ({
          'Page': vc.page || '',
          'Clause #': vc.clause_number || '',
          'Details': vc.details || '',
          'Comment DD/MM/YY': vc.comment_date ? `${vc.comment_date}` : '',
          'Comment': vc.comment || '',
          'Reply': vc.reply || '',
          'Comment 2 DD/MM/YY': vc.comment_2_date ? `${vc.comment_2_date}` : '',
          'Comment 2': vc.comment_2 || '',
          'Reply 2': vc.reply_2 || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      // Auto column widths
      const colWidths = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 18) }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `V${draft.version} Comments`);
      XLSX.writeFile(wb, `Contract_${id}_V${draft.version}_Comments.xlsx`);
  };

  const handleAddComment = async () => {
      if (!newComment.trim()) return;
      try {
          await axios.post(`${API_URL}/contracts/${id}/comments`, { comment: newComment }, { headers });
          setNewComment('');
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to add comment');
      }
  };

  const handleUploadDraft = async () => {
      if (!newDraftFile) return;
      setIsUploading(true);
      try {
          const formData = new FormData();
          formData.append('contract_pdf', newDraftFile);
          
          await axios.post(`${API_URL}/contracts/${id}/drafts`, formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
          setNewDraftFile(null);
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to upload draft');
      } finally {
          setIsUploading(false);
      }
  };

  const handleDownload = (draftId, filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/contracts/${id}/files/${encodedFilename}`;
      window.open(url, '_blank');
  };

  const handlePreview = (filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/contracts/${id}/files/${encodedFilename}?inline=true`;
      setPreviewUrl(url);
  };

  const handleReport = async (draft) => {
      setReportLoading(prev => ({ ...prev, [draft.id]: true }));
      try {
          // First, fetch the file blob and convert it to base64
          const encodedFilename = encodeURIComponent(draft.file_path);
          const fileRes = await fetch(`${API_URL}/contracts/${id}/files/${encodedFilename}`, { headers });
          if (!fileRes.ok) throw new Error("Could not fetch file for report generation");
          const blob = await fileRes.blob();
          
          const base64data = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
          });

          const fetchReport = async () => {
              const response = await fetch(`${API_URL}/chat_contracts/generate_extraction_report`, {
                  method: 'POST',
                  headers: { ...headers, 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      file_name_input: draft.file_path,
                      file_id_input: draft.id,
                      base_64_str_input: base64data
                  })
              });
              
              const contentType = response.headers.get('content-type') || '';
              if (contentType.includes('application/json') || !response.ok) {
                  let data = {};
                  try { data = await response.json(); } catch (e) {}
                  
                  if (data.message === "False" || data.message === false || data.status === "error") {
                      return false; // Signal that we should try generating vector store
                  }
                  
                  throw new Error(data.error || data.message || 'Failed to generate report');
              }
              
              const reportBlob = await response.blob();
              const url = window.URL.createObjectURL(reportBlob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Report_Version_${draft.version}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              return true;
          };

          let success = await fetchReport();
          
          if (!success) {
              await axios.post(`${API_URL}/chat_contracts/generate_vector_store`, {
                  base_64_str_input: base64data,
                  file_name_input: draft.file_path,
                  file_id_input: draft.id,
              }, { headers });

              success = await fetchReport();
              if (!success) {
                  alert("Report generation failed.");
              }
          }
      } catch (err) {
          console.error('Report error:', err);
          alert('Error generating report.');
      } finally {
          setReportLoading(prev => ({ ...prev, [draft.id]: false }));
      }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files[0].type === 'application/pdf') {
        setNewDraftFile(files[0]);
      } else {
        alert('Please upload a PDF file.');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) return <Loading />;
  if (!contract) return <Typography>Contract not found</Typography>;

  const steps = [
      { label: 'New', icon: <NoteAddIcon /> },
      { label: 'In Progress', icon: <PendingActionsIcon /> },
      { label: 'Done by Client', icon: <TaskAltIcon /> },
      { label: 'Signed', icon: <VerifiedUserIcon /> }
  ];

  const activeStep = steps.findIndex(s => s.label === contract.status);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Section */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden'
        }}>
          {/* Background Decorative Circles */}
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1, width: '100%' }}>
                      <Button 
                          startIcon={<ArrowBackIcon />} 
                          onClick={() => navigate('/contracts')} 
                          sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
                      >
                          Back to Contracts
                      </Button>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: 0.3, color: 'white' }}>
                              Contract #{contract.id}
                          </Typography>
                          <Chip 
                              label={contract.status} 
                              sx={{ 
                                  bgcolor: 'white', 
                                  color: '#1e3c72', 
                                  fontWeight: 'bold',
                                  height: 28,
                                  px: 0.5,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }} 
                          />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', opacity: 0.9, color: 'white' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon fontSize="small" sx={{ color: 'white' }} />
                              <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>{contract.client_name}</Typography>
                          </Box>
                          
                          {contract.project_name && (
                              <>
                                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'white' }} />
                                  <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>Project: {contract.project_name}</Typography>
                              </>
                          )}
                      </Box>
                  </Box>

                  {/* QR Code Box */}
                  <Box sx={{ 
                      bgcolor: 'white', 
                      p: 1.5, 
                      borderRadius: 3, 
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      textAlign: 'center',
                      transform: 'rotate(2deg)',
                      transition: 'transform 0.3s',
                      cursor: 'pointer',
                      '&:hover': { transform: 'rotate(0deg) scale(1.05)' }
                  }}>
                      <img 
                          src={`${API_URL}/contracts/${id}/qr`} 
                          alt="QR Code" 
                          style={{ width: 100, height: 100, display: 'block', borderRadius: 4 }} 
                      />
                      <Typography variant="caption" sx={{ color: '#1e3c72', fontSize: '0.75rem', mt: 1, display: 'block', fontWeight: '900', letterSpacing: 1 }}>
                          SCAN ME
                      </Typography>
                  </Box>
              </Box>
          </Box>
        </Box>
      </Grow>

      <Grow in={true} timeout={800}>
        <Box>
            {/* Tabs Area */}
            <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', bgcolor: 'white', border: 'none', mb: 3 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={(e, val) => setTabValue(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }}
                    sx={{ 
                        borderBottom: 1, 
                        borderColor: '#f0f0f0', 
                        bgcolor: 'white', 
                        px: 2,
                        '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' }
                    }}
                >
                    <Tab icon={<ArticleIcon />} iconPosition="start" label="Contract Details" />
                    <Tab icon={<DescriptionIcon />} iconPosition="start" label="Contract Versions" />
                    <Tab icon={<CommentIcon />} iconPosition="start" label="Discussion" />
                </Tabs>

                {/* Main Content Paper */}
                <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '400px' }}>
                
                {/* Contract Details Section */}
                <TabPanel value={tabValue} index={0}>
                  <Grid container spacing={4}>
                      {/* Left Column: Description & Info */}
                      <Grid item xs={12} md={8}>
                          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ mb: 4 }}>
                                  <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>Workflow Progress</Typography>
                                  <Stepper activeStep={activeStep} alternativeLabel>
                                      {steps.map((step, index) => (
                                          <Step key={step.label}>
                                              <StepLabel StepIconComponent={() => (
                                                  <Avatar 
                                                      onClick={() => {
                                                          // Optional: Allow clicking to change status if logic permits
                                                          if (index > activeStep && index === activeStep + 1) handleStatusChange(step.label);
                                                      }}
                                                sx={{ 
                                                    cursor: index === activeStep + 1 ? 'pointer' : 'default',
                                                    bgcolor: activeStep >= index ? 'success.main' : 'grey.300',
                                                    color: 'white',
                                                    width: 40, height: 40
                                                }}
                                            >
                                                {step.icon}
                                            </Avatar>
                                        )}>
                                            {step.label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {activeStep < steps.length - 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => handleStatusChange(steps[activeStep + 1].label)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Mark as {steps[activeStep + 1].label}
                                    </Button>
                                </Box>
                            )}
                        </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="h6" fontWeight="bold" color="text.primary">Description</Typography>
                                  {isEditingDescription ? (
                                      <Box>
                                          <Button size="small" variant="contained" onClick={handleDescriptionSave} startIcon={<SaveIcon />} sx={{ mr: 1, borderRadius: 2 }}>Save</Button>
                                          <Button size="small" variant="outlined" onClick={() => setIsEditingDescription(false)} startIcon={<CloseIcon />} sx={{ borderRadius: 2 }}>Cancel</Button>
                                      </Box>
                                  ) : (
                                      <IconButton size="small" onClick={() => { setEditDescription(contract.description || ''); setIsEditingDescription(true); }}>
                                          <EditIcon fontSize="small" />
                                      </IconButton>
                                  )}
                              </Box>
                              <Divider sx={{ mb: 2 }} />
                              {isEditingDescription ? (
                                  <TextField 
                                      fullWidth
                                      multiline
                                      rows={4}
                                      variant="outlined"
                                      value={editDescription}
                                      onChange={(e) => setEditDescription(e.target.value)}
                                      placeholder="Enter description..."
                                  />
                              ) : (
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', lineHeight: 1.6 }}>
                                      {contract.description || "No description provided."}
                                  </Typography>
                              )}
                    </Box>
                </Grid>

                {/* Right Column: Meta Data */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={4} sx={{ height: '100%' }}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Location & DDP</Typography>
                            <Stack spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LocationOnIcon color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Location</Typography>
                                        <Typography variant="body2" fontWeight="bold">{contract.country}</Typography>
                                        <Typography variant="body2">{contract.division}</Typography>
                                    </Box>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <PendingActionsIcon color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">DDP</Typography>
                                        <Typography variant="body2" fontWeight="bold">{contract.ddp}</Typography>
                                    </Box>
                                </Paper>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Ownership</Typography>
                            <Stack spacing={2}>
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Created By" 
                                    value={contract.created_by} 
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Client" 
                                    value={contract.client_name}
                                    InputProps={{ readOnly: true }}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
                {/* Draft Versions Section */}
                <Box 
                    sx={{ 
                        mb: 5, 
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="primary" fontSize="large" />
                            <Typography variant="h5" fontWeight="bold">Contract Versions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                            <Button variant="outlined" component="label" startIcon={<UploadIcon />} disabled={isUploading} sx={{ flexGrow: { xs: 1, sm: 0 }, whiteSpace: 'nowrap' }}>
                                {newDraftFile ? newDraftFile.name : "Select PDF"}
                                <input type="file" hidden accept="application/pdf" onChange={e => setNewDraftFile(e.target.files[0])} />
                            </Button>
                            <Button variant="contained" disabled={!newDraftFile || isUploading} onClick={handleUploadDraft} sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: 100 }}>
                                {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
                            </Button>
                        </Box>
                    </Box>
                    
                    {/* Drag and Drop Zone */}
                    <Box 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{ 
                            flexGrow: 1,
                            minHeight: 200,
                            border: '2px dashed', 
                            borderColor: isDragging ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            bgcolor: isDragging ? 'action.hover' : 'transparent',
                            p: 2,
                            transition: 'all 0.2s'
                        }}
                    >
                        <Grid container spacing={2}>
                            {contract.drafts && contract.drafts.length > 0 ? (
                                contract.drafts.map((draft) => (
                                    <Grid item xs={12} key={draft.id}>
                                        <Paper 
                                            elevation={0}
                                            sx={{ 
                                                p: 2.5, 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'flex-start', sm: 'center' }, 
                                                justifyContent: 'space-between', 
                                                borderRadius: 3, 
                                                bgcolor: 'background.paper',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                gap: 2,
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                    borderColor: 'primary.main',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                                <Avatar sx={{ bgcolor: 'primary.main', color: 'white', width: 48, height: 48, boxShadow: 2 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold">V{draft.version}</Typography>
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                                                        {draft.file_path}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                        <VerifiedUserIcon sx={{ fontSize: 14 }} /> {draft.created_by} • {new Date(draft.created_at).toLocaleDateString()} at {new Date(draft.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, pt: { xs: 1, sm: 0 } }}>
                                                {isChatbotAllowed && (
                                                    <Button size="small" variant="contained" color="primary" sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }} startIcon={<ChatIcon />} onClick={() => handleChatWithVersion(draft)}>
                                                        Chat
                                                    </Button>
                                                )}
                                                <Button size="small" variant="outlined" color="primary" sx={{ borderRadius: 2, textTransform: 'none' }} startIcon={reportLoading[draft.id] ? <CircularProgress size={16} /> : <ArticleIcon />} onClick={() => handleReport(draft)} disabled={reportLoading[draft.id]}>
                                                    Evaluate
                                                </Button>
                                              
                                                <Button
                                                    size="small"
                                                    variant={expandedDraft === draft.id ? 'contained' : 'outlined'}
                                                    onClick={() => setExpandedDraft(expandedDraft === draft.id ? null : draft.id)}
                                                    startIcon={<CommentIcon />}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                      
                                                        fontWeight: 600,
                                                        ...(expandedDraft === draft.id
                                                            ? { background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 4px 12px rgba(30,60,114,0.25)', border: 'none' }
                                                            : { borderColor: '#1e3c72', color: '#1e3c72', '&:hover': { bgcolor: 'rgba(30,60,114,0.06)', borderColor: '#1e3c72' } }
                                                        )
                                                    }}
                                                >
                                                    Comments{draft.version_comments && draft.version_comments.length > 0 ? ` (${draft.version_comments.length})` : ''}
                                                </Button>
                                                  <IconButton size="small" color="primary" sx={{ bgcolor: 'secondary.50', borderRadius: 2, '&:hover': { bgcolor: 'secondary.100' } }} onClick={() => handlePreview(draft.file_path)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton size="small" color="primary" sx={{ bgcolor: 'primary.50', borderRadius: 2, '&:hover': { bgcolor: 'primary.100' } }} onClick={() => handleDownload(draft.id, draft.file_path)}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Box>
                                        </Paper>

                                        {/* Version Comments Table */}
                                        {expandedDraft === draft.id && (
                                            <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                                        Version {draft.version} Comments
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {draft.version_comments && draft.version_comments.length > 0 && (
                                                            <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => handleExportVersionComments(draft)} sx={{ borderRadius: 2, textTransform: 'none', color: '#16a34a', borderColor: '#16a34a', '&:hover': { bgcolor: '#f0fdf4', borderColor: '#15803d' } }}>
                                                                Export Excel
                                                            </Button>
                                                        )}
                                                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openVcDialog(draft.id)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                                                            Add Row
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow sx={{ bgcolor: '#f4f6f8' }}>
                                                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Page</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Clause</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Comment</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Reply</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Comment 2</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Reply 2</TableCell>
                                                                <TableCell />
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {draft.version_comments && draft.version_comments.length > 0 ? (
                                                                draft.version_comments.map((vc) => (
                                                                    <TableRow key={vc.id} hover>
                                                                        <TableCell>{vc.page}</TableCell>
                                                                        <TableCell>{vc.clause_number}</TableCell>
                                                                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vc.details}</TableCell>
                                                                        <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                            {vc.comment_date && <Typography variant="caption" color="text.secondary" display="block">{vc.comment_date}</Typography>}
                                                                            {vc.comment}
                                                                        </TableCell>
                                                                        <TableCell sx={{ maxWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vc.reply}</TableCell>
                                                                        <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                            {vc.comment_2_date && <Typography variant="caption" color="text.secondary" display="block">{vc.comment_2_date}</Typography>}
                                                                            {vc.comment_2}
                                                                        </TableCell>
                                                                        <TableCell sx={{ maxWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vc.reply_2}</TableCell>
                                                                        <TableCell>
                                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                                <Tooltip title="Edit"><IconButton size="small" onClick={() => openVcDialog(draft.id, vc)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleVcDelete(draft.id, vc.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                                        No comments yet. Click "Add Row" to add one.
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 4, textAlign: 'center', mt: 4 }}>
                                        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="textSecondary">Drag and drop PDF files here</Typography>
                                        <Typography variant="caption" color="textSecondary">or use the Select PDF button above</Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Box sx={{ height: '70vh', flexGrow: 1, display: 'flex', flexDirection: 'column', margin: '0 auto', bgcolor: 'background.paper', borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
                    {/* Header */}
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                            <CommentIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">Discussion</Typography>
                            <Typography variant="caption" color="text.secondary">Collaborate and leave notes</Typography>
                        </Box>
                    </Box>
                    
                    {/* Chat Area */}
                    <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', bgcolor: '#f4f6f8', display: 'flex', flexDirection: 'column', gap: 3 }}>
                         {contract.comments && contract.comments.length > 0 ? (
                            contract.comments.map((comment) => (
                                <Box key={comment.id} sx={{ display: 'flex', gap: 2, maxWidth: '85%' }}>
                                    <Avatar sx={{ width: 40, height: 40, bgcolor: utilHashColor(comment.user), boxShadow: 1, fontWeight: 'bold' }}>
                                        {comment.user.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                                {comment.user}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(comment.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                            </Typography>
                                        </Box>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', borderRadius: '0 16px 16px 16px', border: '1px solid', borderColor: 'rgba(0,0,0,0.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>{comment.comment}</Typography>
                                        </Paper>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.7 }}>
                                <CommentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">No comments yet</Typography>
                                <Typography variant="body2" color="text.disabled">Start the discussion below!</Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Chat Input */}
                    <Box sx={{ p: 2.5, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Paper elevation={0} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 1, pr: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 4, bgcolor: '#fafafa', '&:focus-within': { borderColor: 'primary.main', bgcolor: 'white', boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)' }, transition: 'all 0.2s' }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder="Write a comment..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                                variant="standard"
                                InputProps={{ disableUnderline: true, sx: { px: 1, py: 0.5, fontSize: '0.95rem' } }}
                            />
                            <IconButton 
                                color="primary" 
                                onClick={handleAddComment} 
                                disabled={!newComment.trim()} 
                                sx={{ bgcolor: newComment.trim() ? 'primary.main' : 'action.disabledBackground', color: newComment.trim() ? 'white' : 'action.disabled', '&:hover': { bgcolor: 'primary.dark' }, width: 44, height: 44, borderRadius: 3, mb: 0.5 }}
                            >
                                <SendIcon fontSize="small" sx={{ ml: 0.5 }} />
                            </IconButton>
                        </Paper>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'right', mt: 1, mr: 1 }}>
                            Press Enter to send, Shift + Enter for new line
                        </Typography>
                    </Box>
                </Box>
            </TabPanel>

            </Box>
        </Paper>
      </Box>
      </Grow>

      {/* Chatbot Floating UI (Only on Contract Versions Tab) */}
      {isChatbotAllowed && tabValue === 1 && (
        <>
          {!chatOpen && (
            <Fab
              color="primary"
              aria-label="chat"
              onClick={() => setChatOpen(true)}
              sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000, boxShadow: 6 }}
            >
              <ChatIcon sx={{ fontSize: 32 }} />
            </Fab>
          )}

          {chatOpen && (
            <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: chatMaximized ? 0 : 32,
            right: chatMaximized ? 0 : 32,
            width: chatMaximized ? '100vw' : { xs: '90%', sm: 450 },
            height: chatMaximized ? '100vh' : '70vh',
            maxHeight: chatMaximized ? 'none' : 700,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            borderRadius: chatMaximized ? 0 : 4,
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{
            background: 'linear-gradient(to right, #1976d2, #4791db)',
            color: 'white',
            px: 3,
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 ,color:'white'}}> {activeFile ? `Chat - V${activeFile.version}` : 'Chat'} </Typography>
            </Box>
            <Box>
              <IconButton size="small" onClick={() => setChatMaximized(!chatMaximized)} sx={{ color: 'white', mr: 1 }}>
                {chatMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#f8f9fa' }}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                <Paper
                  elevation={msg.sender === 'user' ? 2 : 1}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: msg.sender === 'user' ? '#1976d2' : 'white',
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                    borderRadius: 3,
                    borderBottomRightRadius: msg.sender === 'user' ? 0 : 3,
                    borderBottomLeftRadius: msg.sender === 'user' ? 3 : 0,
                  }}
                >
                  {msg.isLoader ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} color="inherit" />
                      <Typography variant="body1">{msg.text}</Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{
                        lineHeight: 1.5,
                        '& p': { m: 0, mb: 1, '&:last-of-type': { mb: 0 } },
                        '& pre': { bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1, overflowX: 'auto', my: 1 },
                        '& code': { fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.1)', px: 0.5, borderRadius: 1 },
                        '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                        '& a': { color: 'inherit', textDecoration: 'underline' },
                        whiteSpace: msg.sender === 'user' ? 'pre-wrap' : 'normal'
                      }}
                      dangerouslySetInnerHTML={{ __html: msg.sender === 'user' ? (msg.text || '...') : marked.parse(msg.text || '...') }}
                    />
                  )}
                </Paper>
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5 }}>
            <TextField
              size="medium"
              fullWidth
              placeholder={activeFile ? "Type your message..." : "Select a document to chat"}
              disabled={chatLoading || !activeFile}
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 4 }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={chatLoading || !chatInput.trim() || !activeFile}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                borderRadius: '50%',
                p: 1.5
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
          )}
        </>
      )}

      {/* Document Preview Modal */}
      <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon  /> Document Preview
            </Typography>
            <IconButton onClick={() => setPreviewUrl(null)} sx={{ color: 'white' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {previewUrl && (
                <iframe 
                    src={`${previewUrl}#toolbar=0`} 
                    style={{ width: '100%', height: '100%', border: 'none' }} 
                    title="PDF Preview"
                />
            )}
        </DialogContent>
      </Dialog>

      {/* Version Comment Add/Edit Dialog */}
      <Dialog open={vcDialogOpen} onClose={() => setVcDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
            {vcEditItem ? 'Edit Comment Row' : 'Add Comment Row'}
          </Typography>
          <IconButton size="small" onClick={() => setVcDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>

            {/* Clause Info */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Clause Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Page" value={vcForm.page} onChange={e => setVcForm(f => ({ ...f, page: e.target.value }))} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Clause #" value={vcForm.clause_number} onChange={e => setVcForm(f => ({ ...f, clause_number: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Details" multiline rows={2} value={vcForm.details} onChange={e => setVcForm(f => ({ ...f, details: e.target.value }))} />
                </Grid>
              </Grid>
            </Paper>

            {/* Comment 1 */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Comment</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Comment Date" type="date" value={vcForm.comment_date} onChange={e => setVcForm(f => ({ ...f, comment_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth label="Comment" multiline rows={2} value={vcForm.comment} onChange={e => setVcForm(f => ({ ...f, comment: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Reply" multiline rows={2} value={vcForm.reply} onChange={e => setVcForm(f => ({ ...f, reply: e.target.value }))} />
                </Grid>
              </Grid>
            </Paper>

            {/* Comment 2 */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Comment 2</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Comment 2 Date" type="date" value={vcForm.comment_2_date} onChange={e => setVcForm(f => ({ ...f, comment_2_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth label="Comment 2" multiline rows={2} value={vcForm.comment_2} onChange={e => setVcForm(f => ({ ...f, comment_2: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Reply 2" multiline rows={2} value={vcForm.reply_2} onChange={e => setVcForm(f => ({ ...f, reply_2: e.target.value }))} />
                </Grid>
              </Grid>
            </Paper>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setVcDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button
            onClick={handleVcSave}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 'bold',
              px: 3,
              py: 1,
              background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)',
              boxShadow: '0 4px 12px rgba(30,60,114,0.3)'
            }}
          >
            {vcEditItem ? 'Save Changes' : 'Add Row'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

// Simple hash for avatar colors
const utilHashColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
}

export default ContractDetails;
